import { KdsModal, KdsText, VStack } from "@kazaar-platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useExpertMode, useToasts } from "@/hooks";
import { type ModalValidateProductSelectionProps } from "./ModalValidateProductSelection.types";

export const ModalValidateProductSelection = ({
  isOpen,
  selectedOrderItems,
  onOpenChange,
}: ModalValidateProductSelectionProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const queryClient = useQueryClient();

  const { useAcceptBrief } = useExpertMode();
  const { mutateAsync: acceptBrief, isPending } = useAcceptBrief();

  const handleSubmit = async () => {
    try {
      await Promise.all(
        selectedOrderItems.map((orderItem) =>
          acceptBrief({ orderItemId: orderItem.orderItemId })
        )
      );
      add({
        state: "success",
        title: t("expertMode.modalValidateProductSelection.toast.success"),
      });
      queryClient.invalidateQueries({
        queryKey: ["order"],
      });
      queryClient.invalidateQueries({
        queryKey: ["orders"],
      });
      onOpenChange();
    } catch (error) {
      console.error(error);
      add({
        state: "error",
        title: t("expertMode.modalValidateProductSelection.toast.error"),
      });
    }
  };

  return (
    <KdsModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          title: t("expertMode.modalValidateProductSelection.title"),
        },
        content: (
          <VStack gap="sp-md">
            <KdsText size="subtitle-md">
              {t("expertMode.modalValidateProductSelection.description")}
            </KdsText>

            <VStack>
              {selectedOrderItems.map((orderItem) => (
                <KdsText key={orderItem.orderItemId} size="subtitle-md">
                  • {orderItem.name}
                </KdsText>
              ))}
            </VStack>

            <KdsText>
              {t("expertMode.modalValidateProductSelection.notifiedByEmail")}
            </KdsText>
          </VStack>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.cancel"),
              variant: "ghost",
              color: "neutral",
              isLoading: isPending,
              disabled: isPending,
              onPress: onOpenChange,
            },
            {
              disabled: isPending,
              isLoading: isPending,
              children: t("buttons.confirm"),
              onPress: handleSubmit,
            },
          ],
        },
      }}
    />
  );
};
